import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import { Box, Button, Container, Paper, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { ReactNode, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useLogger } from '../../hooks/useLogger';

export interface ErrorPageAction {
    label: string;
    icon: ReactNode;
    onClick: () => void;
}

export const ErrorPage = ({
    title,
    message,
    action,
    isTransient = false,
}: {
    title: string;
    message: string;
    action?: ErrorPageAction;
    // Transient errors (e.g. connection problems on flaky mobile networks)
    // are logged as warnings to keep error logs actionable.
    isTransient?: boolean;
}) => {
    const intl = useIntl();
    const logger = useLogger();

    useEffect(() => {
        const logMessage = `Error Page was displayed to the user with title: ${title} and message: ${message}`;
        if (isTransient) {
            logger.warn(logMessage);
        } else {
            logger.error(logMessage);
            Sentry.captureMessage(
                `Error Page displayed: ${title} / ${message}`,
                {
                    level: 'error',
                    tags: { errorPageTitle: title, errorPageMessage: message },
                },
            );
        }
    }, [logger, title, message, isTransient]);

    const handleReturn = () => {
        window.location.href = '/';
    };

    return (
        <Box
            sx={{
                boxSizing: 'border-box',
                minHeight: {
                    xs: 'calc(100vh - 56px)',
                    sm: 'calc(100vh - 64px)',
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 3, sm: 5 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 72,
                            height: 72,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'warning.light',
                            color: 'warning.contrastText',
                        }}
                    >
                        <LinkOffOutlinedIcon sx={{ fontSize: 40 }} />
                    </Box>

                    <Typography
                        variant="h5"
                        component="h1"
                        align="center"
                        fontWeight={600}
                    >
                        {intl.formatMessage({ id: title })}
                    </Typography>

                    <Typography
                        variant="body1"
                        color="text.secondary"
                        align="center"
                    >
                        {intl.formatMessage({ id: message })}
                    </Typography>

                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={action?.icon ?? <HomeOutlinedIcon />}
                        onClick={action?.onClick ?? handleReturn}
                        sx={{ mt: 1 }}
                    >
                        {intl.formatMessage({
                            id: action?.label ?? 'return_home',
                        })}
                    </Button>
                </Paper>
            </Container>
        </Box>
    );
};
